import React from "react";
import PropTypes from "prop-types";
import {
  Box  
} from "@mui/material";
import ChatBubble from "../ChatBubble";
import bgicon from "../../assets/images/bgimage.png";

const ChatMessagesContainer = ({
  activeTab,
  chatContexts,
  messages,
  scrollBoxRef,
  handleStartNewChat,
  handleCitationClickOpen,
  handleCitationClose,
  citationopen,
  setCitationOpen,
  tables,
  handleDownloadAll,
  citationclickvisible,
  handleRegenerateResponse,
  onAbbreviationCandidateClick,
  onSubmitFeedback,
}) => {
  // Helper function to generate unique message key
  const generateMessageKey = (message, index) => {
    return message.timestamp 
      ? `${message.timestamp}-${message.isUser}-${index}`
      : `${message.text?.slice(0, 50)}-${message.isUser}-${index}`;
  };

  // Helper function to get message box styling
  const getMessageBoxStyling = (message) => {
    const commonStyles = {
      borderRadius: "12px",
      marginLeft: message.isUser ? "auto" : "2%",
      marginRight: message.isUser ? "2%" : "auto",
      wordWrap: "break-word",
      overflowWrap: "anywhere",
      alignSelf: message.isUser ? "flex-end" : "flex-start",
    };

    return !message.isSystemNotification
      ? { ...commonStyles, maxWidth: "85%", width: "auto" }
      : { ...commonStyles, width: "90%", marginLeft: "auto", marginRight: "auto", alignSelf: "center" };
  };

  // Helper function to determine if regenerate should be enabled
  const shouldShowRegenerate = (message, index) => {
    if (message.isUser || message.isSystemNotification) return false;
    
    const lastBotMessageIndex = messages.findLastIndex(
      (msg) => !msg.isUser && !msg.isSystemNotification
    );
    
    return index === lastBotMessageIndex;
  };

  return (
    <Box
      sx={{
        flexGrow: 1,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Chat background */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundImage: `url(${bgicon})`,
          zIndex: 0,
        }}
      />

      {/* Chat messages container */}
      <Box
        ref={scrollBoxRef}
        sx={{
          height: 'calc(100vh - 119px - 100px)', // Adjust for header, tabs, and input
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          padding: "20px 1% 0 1%",
          position: "relative",
          zIndex: 2,
        }}
      >

        {/* Message bubbles */}
        {messages.map((message, index) => (
          <Box
            key={generateMessageKey(message, index)}
            sx={getMessageBoxStyling(message)}
          >
            <ChatBubble
              message={message.text}
              additionalAgentResponses={
                message?.additionalAgentResponses || []
              }
              responseTime={message?.responseTime}
              isUser={message.isUser}
              isSystemNotification={message.isSystemNotification}
              handleCitationClickOpen={handleCitationClickOpen}
              handleCitationClose={handleCitationClose}
              citationopen={citationopen}
              setCitationOpen={setCitationOpen}
              tables={tables}
              handleDownloadAll={handleDownloadAll}
              citmessage={message.citations}
              isLoading={message.isLoading || false}
              citationclickvisible={citationclickvisible}
              isFirstMessage={index === 0 && !message.isUser}
              toolResult={message?.citations}
              selectDatasourse={message?.selectDatasourse}
              additional_agent_responses={message?.additional_agent_responses}
              reformatted_question={message?.reformatted_question}
              questionId={message?.question_id}
              threadId={message?.thread_id}
              feedbackType={message?.feedback_type}
              feedbackReason={message?.feedback_reason}
              feedbackComment={message?.feedback_comment}
              visualization_payload={message?.visualization_payload}
              index={index}
              onRegenerateClick={
                shouldShowRegenerate(message, index) ? handleRegenerateResponse : undefined
              }
              onAbbreviationCandidateClick={onAbbreviationCandidateClick}
              onSubmitFeedback={onSubmitFeedback}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const areChatMessageContainerPropsEqual = (prevProps, nextProps) => {
  return (
    prevProps.activeTab === nextProps.activeTab &&
    prevProps.chatContexts === nextProps.chatContexts &&
    prevProps.messages === nextProps.messages &&
    prevProps.scrollBoxRef === nextProps.scrollBoxRef &&
    prevProps.citationopen === nextProps.citationopen &&
    prevProps.tables === nextProps.tables &&
    prevProps.citationclickvisible === nextProps.citationclickvisible
  );
};

ChatMessagesContainer.propTypes = {
  activeTab: PropTypes.string,
  chatContexts: PropTypes.array,
  messages: PropTypes.array.isRequired,
  scrollBoxRef: PropTypes.object,
  handleStartNewChat: PropTypes.func,
  handleCitationClickOpen: PropTypes.func,
  handleCitationClose: PropTypes.func,
  citationopen: PropTypes.bool,
  setCitationOpen: PropTypes.func,
  tables: PropTypes.object,
  handleDownloadAll: PropTypes.func,
  citationclickvisible: PropTypes.bool,
  handleRegenerateResponse: PropTypes.func,
  onAbbreviationCandidateClick: PropTypes.func,
  onSubmitFeedback: PropTypes.func,
};

export default React.memo(ChatMessagesContainer, areChatMessageContainerPropsEqual);
